import Swal from "sweetalert2";
import React, { useState, useEffect, useRef } from "react";

import { Toast } from "react-bootstrap";
import MindfulnessItem from "./MindfulnessItem";
import EditMindfulness from "./Modals/EditMindfulness";
import AddMindfulness from "./Modals/AddMindfulness";
import {
  CREATE_MINDFULNESS,
  DELETE_MINDFULNESS,
  GET_MINDFULNESS,
  UPDATE_MINDFULNESS,
  UPDATE_MINDFULNESS_AVAILABILITY,
  UPDATE_MINDFULNESS_POSITION,
} from "URLRoutes";
import axios from "axios";
import { generateURLOptions } from "URLOptions";
import { TiPlus } from "react-icons/ti";
import FilledButton from "components/InterstellrComponents/FilledButton";

import { useDraggable, useDroppable, Overlay } from "snapdrag";

const GRID_COLUMNS = 5;
const GAP = 8;

// --- Draggable Item ---
const MindfulnessItemDraggable = ({
  item,
  index,
  kind,
  onEdit,
  onDelete,
  onPlay,
  onPause,
  onChangeStatus,
}) => {
  const { draggable, isDragging } = useDraggable({
    kind,
    data: { id: item._id, item, index },
    move: true, // use Overlay
  });

  return draggable(
    <div
      className="border p-2 bg-white rounded-md"
      style={{ opacity: isDragging ? 0.2 : 1 }}
    >
      <MindfulnessItem
        item={item}
        onEdit={onEdit}
        onDelete={onDelete}
        onPlay={onPlay}
        onPause={onPause}
        onChangeStatus={onChangeStatus}
      />
    </div>
  );
};

const Mindfulness = () => {
  const [mindfulness, setMindfulness] = useState([]);

  const [showAddMindfulness, setShowAddMindfulness] = useState(false);
  const [selectedMindfulness, setSelectedMindfulness] = useState(null);
  const [showEditMindfulness, setShowEditMindfulness] = useState(false);

  const [showAudio, setShowAudio] = useState(false);
  const [playAudio, setPlayAudio] = useState("");

  const handleAudioPlay = (event) => {
    setPlayAudio(event);
    setShowAudio(true);
  };

  const handleAudioStop = () => {
    setPlayAudio("");
    setShowAudio(false);
  };

  const containerRef = useRef(null);
  const kind = "MINDFULNESS";

  useEffect(() => {
    document.title = "Mindfulness | Administration Panel | Mind Pulse ";
    getMindfulness();
  }, []);

  const getMindfulness = async () => {
    try {
      const response = await axios.get(GET_MINDFULNESS, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      setMindfulness(response?.data?.data);
    } catch (error) {
      console.error("Get Mindfulness Items Error: ", error);
      alert(error);
    }
  };

  const addMindfulness = async (data) => {
    try {
      const response = await axios.post(CREATE_MINDFULNESS, data, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      setShowAddMindfulness(false);
      setTimeout(() => {
        getMindfulness();
      }, 250);
    } catch (error) {
      console.error("Add Mindfulness Error: ", error);
      alert(error);
    }
  };

  const updateMindfulness = async (data) => {
    try {
      const response = await axios.post(UPDATE_MINDFULNESS, data, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      setShowEditMindfulness(false);
      setTimeout(() => {
        getMindfulness();
      }, 250);
    } catch (error) {
      console.error("Update Mindfulness Error: ", error);
      alert(error);
    }
  };

  const updateMindfulnessAvailability = async (data) => {
    try {
      const response = await axios.post(UPDATE_MINDFULNESS_AVAILABILITY, data, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      getMindfulness();
    } catch (error) {
      console.error("Update Mindfulness Availability Error: ", error);
      alert(error);
    }
  };

  const updateMindfulnessPosition = async (data) => {
    console.log("Mindfulness Data: ", data);
    try {
      const response = await axios.post(UPDATE_MINDFULNESS_POSITION, data, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      getMindfulness();
    } catch (error) {
      console.error("Update Mindfulness Position Error: ", error);
      alert(error);
    }
  };

  const deleteMindfulness = (id) => {
    Swal.fire({
      title: "Delete Mindfulness Item",
      text: "Deleting a Mindfulness Item is irreversible. Please confirm you want to delete this item.",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Delete Mindfulness Item",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.post(
            DELETE_MINDFULNESS,
            { itemId: id },
            { headers: generateURLOptions() }
          );
          if (response?.data?.success === false) {
            return alert(response?.data?.message ?? "");
          }
          getMindfulness();
        } catch (error) {
          console.error("Delete Mindfulness Error:", error);
          alert(error);
        }
      }
    });
  };

  const { droppable } = useDroppable({
    accepts: kind,
    data: {},
    onDrop: ({ data, event }) => {
      const sourceId = data.id;
      const newList = [...mindfulness];
      const moving = newList.find((m) => String(m._id) === String(sourceId));
      if (!moving) return;

      // --- grid index calculation ---
      const gridRect = containerRef.current.getBoundingClientRect();
      const cell = containerRef.current.children[0]?.getBoundingClientRect();
      if (!cell) return;

      const cellWidth = cell.width + GAP;
      const cellHeight = cell.height + GAP;

      const col = Math.floor((event.clientX - gridRect.left) / cellWidth);
      const row = Math.floor((event.clientY - gridRect.top) / cellHeight);

      let targetIndex = row * GRID_COLUMNS + col;
      if (targetIndex < 0) targetIndex = 0;
      if (targetIndex >= newList.length) targetIndex = newList.length - 1;

      console.log("Dragged:", sourceId, "Dropped at index:", targetIndex);

      const oldIndex = newList.findIndex(
        (m) => String(m._id) === String(sourceId)
      );
      newList.splice(oldIndex, 1);
      newList.splice(targetIndex, 0, moving);
      updateMindfulnessPosition({
        orderedIds: newList.map((e) => e._id),
      });
    },
  });

  return (
    <>
      <AddMindfulness
        isOpen={showAddMindfulness}
        onClose={() => setShowAddMindfulness(false)}
        onSubmit={addMindfulness}
      />
      <EditMindfulness
        isOpen={showEditMindfulness}
        onClose={() => setShowEditMindfulness(false)}
        item={selectedMindfulness}
        onSubmit={updateMindfulness}
      />

      <div className="w-full px-8">
        {/* Heading */}
        <div className="flex flex-row mt-8">
          <div className="flex flex-col">
            <h4 className="font-medium text-lg">Mindfulness Sessions</h4>
            <p className="font-light text-base">
              {mindfulness?.length ?? 0} Items
            </p>
          </div>
          <div className="flex-1" />
          <div className="w-1/6">
            <FilledButton
              text="Add"
              icon={<TiPlus className="text-white mr-2" />}
              onPress={() => setShowAddMindfulness(true)}
              borderRadius={96}
            />
          </div>
        </div>

        {/* Grid with Snapdrag */}
        {droppable(
          <div ref={containerRef} className="grid grid-cols-5 gap-2">
            {mindfulness?.map((item, index) => (
              <MindfulnessItemDraggable
                key={item._id}
                item={item}
                index={index}
                kind={kind}
                onEdit={(data) => {
                  setSelectedMindfulness(data);
                  setShowEditMindfulness(true);
                }}
                onDelete={deleteMindfulness}
                onPlay={handleAudioPlay}
                onPause={handleAudioStop}
                onChangeStatus={updateMindfulnessAvailability}
              />
            ))}
          </div>
        )}
      </div>

      {/* 👇 Global Overlay */}
      <Overlay>
        {({ data }) => {
          if (!data?.item) return null;
          return (
            <div className="border p-2 bg-white rounded-md shadow-xl opacity-90">
              <MindfulnessItem
                item={data.item}
                onEdit={setSelectedMindfulness}
                onDelete={deleteMindfulness}
                onPlay={setPlayAudio}
                onPause={() => setPlayAudio("")}
                onChangeStatus={updateMindfulnessAvailability}
              />
            </div>
          );
        }}
      </Overlay>

      <Toast onClose={() => setPlayAudio("")} show={showAudio} delay={3000}>
        <Toast.Body>
          <Toast.Header className="border-0 p-0 pe-2">
            <audio
              src={playAudio?.audio ?? ""}
              controls
              autoPlay
              preload="auto"
            />
          </Toast.Header>
        </Toast.Body>
      </Toast>
    </>
  );
};

export default Mindfulness;
